import React, { useState, useEffect } from 'react';
import LoadingSpinner from '../../common/LoadingSpinner/LoadingSpinner';
import {
  Inbox, Clipboard, Save, RefreshCcw, Calendar, IdCard, Banknote, SquareCheckBig
} from 'lucide-react';
import { estadosDisponibles } from '@/config/constants';
import { useNotifications } from '@/hooks/useNotifications';

const StatusManagementPanel = () => {
  const { success, info, warning, error } = useNotifications();

  const [tramiteRequests, setTramiteRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedTramite, setSelectedTramite] = useState(null);

  useEffect(() => {
    fetchTramiteRequests();
  }, [selectedStatus]);

  const fetchTramiteRequests = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/applications/my-requests`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        let data = await response.json();

        // Filtrar por estado si no es 'all'
        if (selectedStatus !== 'all') {
          data = data.filter(tr => tr.status === selectedStatus);
        }

        setTramiteRequests(data);
      }
    } catch (error) {
      error('Error fetching tramite requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (tramiteId, newStatus, notes = '') => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/applications/${tramiteId}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status: newStatus,
          notes: notes
        })
      });

      if (response.ok) {
        setShowStatusModal(false);
        success(`Estado actualizado a: ${newStatus}`);
        fetchTramiteRequests();
        setSelectedTramite(null);
      } else {
        const error = await response.json();
        error(`Error: ${error.detail}`);
      }
    } catch (error) {
      error('Error actualizando estado');
    }
  };

  const openStatusModal = (tramite) => {
    setSelectedTramite(tramite);
    setShowStatusModal(true);
  };

  const getStatusInfo = (status) => {
    return estadosDisponibles.find(e => e.id === status) ||
      { name: status, icon: '❓', color: 'gray' };
  };

  if (loading) {
    return <LoadingSpinner message="Cargando gestión de estados..." />;
  }

  return (
    <div className="space-y-8">
      {/* Header con filtros */}
      <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-2xl p-8">
        <h1 className="text-3xl font-bold mb-4">Gestión de Estados de Trámites</h1>
        <div className="flex flex-wrap gap-2">
          {estadosDisponibles.slice(0, 6).map((estado) => (
            <button
              key={estado.id}
              onClick={() => setSelectedStatus(estado.id)}
              className={`px-4 py-2 rounded-lg transition-colors ${selectedStatus === estado.id
                ? 'bg-white text-purple-600 font-medium'
                : 'bg-white bg-opacity-20 text-white hover:bg-opacity-30'
                }`}
            >
              {estado.icon} {estado.name}
            </button>
          ))}
        </div>
      </div>

      {/* Estadísticas por estado */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        {estadosDisponibles.slice(1, 7).map((estado) => {
          const count = tramiteRequests.filter(tr => tr.status === estado.id).length;
          return (
            <StatusStatCard
              key={estado.id}
              title={estado.name}
              value={count}
              icon={estado.icon}
              color={estado.color}
              isSelected={selectedStatus === estado.id}
              onClick={() => setSelectedStatus(estado.id)}
            />
          );
        })}
      </div>

      {/* Lista de trámites */}
      <div className="bg-white rounded-2xl shadow-lg p-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900">
            Trámites {selectedStatus === 'all' ? 'Todos' : getStatusInfo(selectedStatus).name}
          </h2>
          <div className="text-sm text-gray-500">
            {tramiteRequests.length} resultados
          </div>
        </div>

        {tramiteRequests.length > 0 ? (
          <div className="space-y-4">
            {tramiteRequests.map((tramite) => (
              <TramiteStatusCard
                key={tramite.id}
                tramite={tramite}
                onChangeStatus={() => openStatusModal(tramite)}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="text-6xl mb-4"><Clipboard /></div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No hay trámites en este estado
            </h3>
            <p className="text-gray-500">
              Los trámites aparecerán aquí cuando cambien a este estado
            </p>
          </div>
        )}
      </div>

      {/* Modal de cambio de estado */}
      {showStatusModal && selectedTramite && (
        <StatusChangeModal
          tramite={selectedTramite}
          estadosDisponibles={estadosDisponibles}
          onStatusChange={handleStatusChange}
          onClose={() => {
            setShowStatusModal(false);
            setSelectedTramite(null);
          }}
        />
      )}
    </div>
  );
};

const StatusStatCard = ({ title, value, icon, color, isSelected, onClick }) => {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600 border-blue-200',
    green: 'bg-green-50 text-green-600 border-green-200',
    yellow: 'bg-yellow-50 text-yellow-600 border-yellow-200',
    red: 'bg-red-50 text-red-600 border-red-200',
    purple: 'bg-purple-50 text-purple-600 border-purple-200',
    orange: 'bg-orange-50 text-orange-600 border-orange-200'
  };

  return (
    <div
      className={`p-4 rounded-2xl border-2 cursor-pointer transition-all hover:scale-105 ${isSelected
        ? 'border-gray-800 shadow-lg scale-105'
        : colorClasses[color] || 'bg-gray-50 text-gray-600 border-gray-200'
        }`}
      onClick={onClick}
    >
      <div className="text-center">
        <div className="text-2xl mb-2 flex justify-center">{icon}</div>
        <div className="text-2xl font-bold">{value}</div>
        <div className="text-xs font-medium opacity-75">{title}</div>
      </div>
    </div>
  );
};

const TramiteStatusCard = ({ tramite, onChangeStatus }) => {
  const statusInfo = {
    'cotizacion_solicitada': { icon: <Clipboard />, color: 'blue', bg: 'bg-blue-50', text: 'text-blue-800' },
    'cotizacion_enviada': { icon: <Inbox />, color: 'indigo', bg: 'bg-indigo-50', text: 'text-indigo-800' },
    'pago_procesado': { icon: '💳', color: 'green', bg: 'bg-green-50', text: 'text-green-800' },
    'documentos_en_revision': { icon: '👀', color: 'blue', bg: 'bg-blue-50', text: 'text-blue-800' },
    'en_proceso': { icon: '🔄', color: 'purple', bg: 'bg-purple-50', text: 'text-purple-800' },
    'completado': { icon: <SquareCheckBig />, color: 'green', bg: 'bg-green-50', text: 'text-green-800' }
  };

  const info = statusInfo[tramite.status] || { icon: '❓', color: 'gray', bg: 'bg-gray-50', text: 'text-gray-800' };

  return (
    <div className={`border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow ${info.bg}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4 flex-1">
          <div className="text-3xl">{info.icon}</div>
          <div className="flex-1">
            <div className="flex items-center space-x-3 mb-2">
              <h3 className="text-lg font-semibold text-gray-900">
                {tramite.tramite_name || 'Trámite'}
              </h3>
              <span className={`px-3 py-1 rounded-full text-xs font-medium border ${info.bg} ${info.text}`}>
                {tramite.status}
              </span>
            </div>

            <p className="text-gray-600 text-sm mb-3">{tramite.description}</p>

            <div className="flex items-center space-x-6 text-sm text-gray-500">
              <span className='flex row items-center'><Calendar className='mr-1' /> {new Date(tramite.created_at).toLocaleDateString()}</span>
              <span className='flex row items-center'><IdCard className='mr-1' /> {tramite._id.substring(0, 8)}...</span>
              {tramite.quoted_price && (
                <span><Banknote /> ${tramite.quoted_price} MXN</span>
              )}
            </div>
          </div>
        </div>

        <div className="ml-4">
          <button
            onClick={onChangeStatus}
            className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium flex row items-center"
          >
            <RefreshCcw className='mr-1' /> Cambiar Estado
          </button>
        </div>
      </div>
    </div>
  );
};

const StatusChangeModal = ({ tramite, estadosDisponibles, onStatusChange, onClose }) => {
  const [selectedStatus, setSelectedStatus] = useState(tramite.status);
  const [notes, setNotes] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    onStatusChange(tramite._id, selectedStatus, notes);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full mx-4">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-bold text-gray-900 flex row items-center">
              <RefreshCcw className='mr-1' /> Cambiar Estado del Trámite
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl"
            >
              ✕
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="mb-6">
            <h4 className="font-medium text-gray-900 mb-2">Trámite:</h4>
            <p className="text-gray-600">{tramite.tramite_name || 'Trámite'}</p>
            <p className="text-sm text-gray-500">Estado actual: {tramite.status}</p>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Nuevo Estado
            </label>
            <div className="grid grid-cols-2 gap-3">
              {estadosDisponibles.slice(1).map((estado) => (
                <label
                  key={estado.id}
                  className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${selectedStatus === estado.id
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                    }`}
                >
                  <input
                    type="radio"
                    name="status"
                    value={estado.id}
                    checked={selectedStatus === estado.id}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                    className="w-4 h-4 text-blue-600 mr-3"
                  />
                  <span className="text-xl mr-2">{estado.icon}</span>
                  <span className="text-sm font-medium">{estado.name}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notas del Cambio (Opcional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent h-24"
              placeholder="Explica el motivo del cambio de estado..."
            />
          </div>

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex row items-center gap-2"
            >
              <Save /> Actualizar Estado
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StatusManagementPanel;